/**
 * Upload Wizard
 * Multi-step upload flow: Select → Preview → Importing → Summary
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import { X, Upload, AlertTriangle, Check, Loader2, FileWarning, Users, ArrowRight, History } from 'lucide-react';
import { Button } from '../shadcn/button';
import { parseCSV, buildExistingPatientsMap, getPreviewRows } from '../../utils/csvParser';
import { calculateBatchRisk, getRiskStats } from '../../utils/riskRules';
import { translateAllFlags, getPrimaryFlag, generateSuggestedApproach } from '../../utils/flagTranslator';
import { api } from '../../lib/api';
import { getCurrentUserWithClinic } from '../../lib/getCurrentUserWithClinic';

// Wizard steps
const STEPS = {
  SELECT: 'select',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  SUMMARY: 'summary',
  ERROR: 'error',
};

const UploadWizard = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState(STEPS.SELECT);
  const [parseResult, setParseResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [importProgress, setImportProgress] = useState(0);
  const [fileName, setFileName] = useState(null);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    setStep(STEPS.SELECT);
    setParseResult(null);
    setImportResult(null);
    setError(null);
    setImportProgress(0);
    setFileName(null);
    onClose();
  }, [onClose]);

  // Handle file selection
  const handleFileSelect = useCallback(async (selectedFile) => {
    setError(null);
    setFileName(selectedFile.name);

    try {
      // Get clinic context
      const { clinicId } = await getCurrentUserWithClinic();

      // Fetch existing patients for duplicate detection
      const patientsData = await api.getPatients();
      const existingPatients = patientsData.patients || patientsData || [];

      const existingMap = buildExistingPatientsMap(existingPatients, clinicId);

      // Parse CSV
      Papa.parse(selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            setError({
              type: 'PARSE_ERROR',
              message: 'Couldn\'t read this file. Make sure it\'s a valid CSV.',
            });
            setStep(STEPS.ERROR);
            return;
          }

          const headers = results.meta.fields || [];
          const parsed = parseCSV(results.data, headers, existingMap, clinicId);

          if (!parsed.success) {
            setError({
              type: parsed.error,
              message: parsed.message,
            });
            setStep(STEPS.ERROR);
            return;
          }

          if (parsed.patients.length === 0) {
            setError({
              type: 'NO_PATIENTS',
              message: 'No valid patients found in this file. All rows were skipped.',
            });
            setStep(STEPS.ERROR);
            return;
          }

          setParseResult(parsed);
          setStep(STEPS.PREVIEW);
        },
        error: () => {
          setError({
            type: 'PARSE_ERROR',
            message: 'Couldn\'t read this file. Make sure it\'s a valid CSV.',
          });
          setStep(STEPS.ERROR);
        },
      });
    } catch (err) {
      console.error('File processing error:', err);
      setError({
        type: 'UNKNOWN_ERROR',
        message: 'Something went wrong. Please try again.',
      });
      setStep(STEPS.ERROR);
    }
  }, []);

  // Handle import
  const handleImport = useCallback(async () => {
    if (!parseResult?.patients?.length) return;

    setStep(STEPS.IMPORTING);
    setImportProgress(10);

    try {
      // Get clinic rules for risk calculation
      const rulesData = await api.getClinicRules();
      const clinicRules = rulesData.rules || [];

      // Build rule config map
      const ruleConfig = {};
      for (const rule of clinicRules) {
        if (rule.rule_code) {
          ruleConfig[rule.rule_code] = rule.enabled === false ? false : rule.config;
        }
      }

      setImportProgress(20);

      // Calculate risk for all patients
      const patientsWithRisk = calculateBatchRisk(parseResult.patients, ruleConfig);

      // Translate EHR flags into human-readable sentences
      const patientsWithFlags = patientsWithRisk.map(patient => {
        const translated = translateAllFlags(patient);
        const primary = getPrimaryFlag(translated);
        return {
          ...patient,
          translated_flags: translated.length > 0 ? translated : undefined,
          primary_flag: primary?.display_text || undefined,
          suggested_approach: generateSuggestedApproach(patient, translated) || undefined,
        };
      });

      setImportProgress(40);

      // Bulk create patients via API (pass file name for import tracking)
      const response = await api.createPatients(patientsWithFlags, fileName);
      const inserted = response.patients || [];

      setImportProgress(95);

      // Update clinic's first_upload_at if this is their first upload
      await api.updateClinic({ first_upload_at: new Date().toISOString() });

      setImportProgress(100);

      // Calculate final stats
      const riskStats = getRiskStats(inserted);

      setImportResult({
        imported: inserted.length,
        riskStats,
        missingContact: parseResult.stats.missingContact,
        defaultedRisk: riskStats.defaulted,
        skipped: parseResult.skipped,
        importRunId: response.import_run_id || null,
      });

      setStep(STEPS.SUMMARY);
    } catch (err) {
      console.error('Import error:', err);
      setError({
        type: 'IMPORT_ERROR',
        message: 'Failed to import patients. Please try again.',
      });
      setStep(STEPS.ERROR);
    }
  }, [parseResult, fileName]);

  // Handle finish
  const handleFinish = useCallback(() => {
    if (onComplete) {
      onComplete(importResult);
    }
    handleClose();
  }, [onComplete, importResult, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-11/12 max-w-lg relative shadow-xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">
            {step === STEPS.SELECT && 'Upload patient list'}
            {step === STEPS.PREVIEW && 'Preview import'}
            {step === STEPS.IMPORTING && 'Importing patients'}
            {step === STEPS.SUMMARY && 'Import complete'}
            {step === STEPS.ERROR && 'Upload failed'}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* SELECT STEP */}
          {step === STEPS.SELECT && (
            <SelectStep onFileSelect={handleFileSelect} />
          )}

          {/* PREVIEW STEP */}
          {step === STEPS.PREVIEW && parseResult && (
            <PreviewStep
              result={parseResult}
              onCancel={() => setStep(STEPS.SELECT)}
              onImport={handleImport}
            />
          )}

          {/* IMPORTING STEP */}
          {step === STEPS.IMPORTING && (
            <ImportingStep progress={importProgress} />
          )}

          {/* SUMMARY STEP */}
          {step === STEPS.SUMMARY && importResult && (
            <SummaryStep
              result={importResult}
              onFinish={handleFinish}
            />
          )}

          {/* ERROR STEP */}
          {step === STEPS.ERROR && error && (
            <ErrorStep
              error={error}
              onRetry={() => {
                setStep(STEPS.SELECT);
                setError(null);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// SELECT STEP
const SelectStep = ({ onFileSelect }) => {
  const fileInputRef = React.useRef();
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      onFileSelect(file);
    }
  };

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center transition-colors
          ${isDragging ? 'border-slate-400 bg-slate-50' : 'border-slate-200'}
        `}
      >
        <Upload className="h-10 w-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
        <p className="text-sm text-slate-600 mb-1">
          Drag & drop a CSV file here
        </p>
        <p className="text-xs text-slate-400 mb-4">
          or click to browse
        </p>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          onChange={handleChange}
          className="hidden"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="text-sm"
        >
          Choose File
        </Button>
      </div>

      <p className="text-xs text-slate-400 text-center">
        Works with exports from Epic, Cerner, Athena, and more
      </p>
    </div>
  );
};

// PREVIEW STEP
const PreviewStep = ({ result, onCancel, onImport }) => {
  const previewRows = getPreviewRows(result.patients, 3);
  const { stats, skipped } = result;

  return (
    <div className="space-y-5">
      {/* Patient count */}
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-slate-400" />
        <span className="text-sm font-medium text-slate-900">
          Found {result.patients.length} patients
        </span>
      </div>

      {/* Preview table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Name</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Contact</th>
              <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Last Visit</th>
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-b-0">
                <td className="px-3 py-2 text-slate-900">{row.name}</td>
                <td className="px-3 py-2 text-slate-600">{row.contact}</td>
                <td className="px-3 py-2 text-slate-600">{row.lastVisit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Warnings */}
      <div className="space-y-2">
        {stats.missingContact > 0 && (
          <div className="flex items-center gap-2 text-amber-600 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>{stats.missingContact} missing contact info</span>
          </div>
        )}
        {stats.missingVisitDate > 0 && (
          <div className="flex items-center gap-2 text-amber-600 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>{stats.missingVisitDate} missing visit date</span>
          </div>
        )}
        {skipped.deceased.length > 0 && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span>{skipped.deceased.length} deceased patients will be skipped</span>
          </div>
        )}
        {skipped.duplicate.length > 0 && (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <span>{skipped.duplicate.length} duplicates will be skipped</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onImport} className="bg-slate-900 hover:bg-slate-800 text-white">
          Import {result.patients.length} patients
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

// IMPORTING STEP
const ImportingStep = ({ progress }) => {
  return (
    <div className="py-8 text-center">
      <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto mb-4" />
      <p className="text-sm font-medium text-slate-900 mb-2">
        Importing patients...
      </p>
      <p className="text-xs text-slate-500 mb-4">
        Calculating risk priorities
      </p>
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-slate-900 transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// SUMMARY STEP
const SummaryStep = ({ result, onFinish }) => {
  const { riskStats, missingContact, defaultedRisk } = result;

  return (
    <div className="space-y-5">
      {/* Success message */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
          <Check className="h-5 w-5 text-emerald-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-900">
            {result.imported} patients imported
          </p>
          <p className="text-xs text-slate-500">
            and prioritized for outreach
          </p>
        </div>
      </div>

      {/* Risk breakdown */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-red-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{riskStats.high}</p>
          <p className="text-xs text-red-600">High</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">{riskStats.medium}</p>
          <p className="text-xs text-amber-600">Medium</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{riskStats.low}</p>
          <p className="text-xs text-emerald-600">Low</p>
        </div>
      </div>

      {/* Warnings */}
      <div className="space-y-2">
        {defaultedRisk > 0 && (
          <div className="flex items-start gap-2 text-amber-600 text-sm bg-amber-50 rounded-lg p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {defaultedRisk} patients couldn't be prioritized — defaulted to Needs Attention
            </span>
          </div>
        )}
        {missingContact > 0 && (
          <div className="flex items-start gap-2 text-slate-600 text-sm bg-slate-50 rounded-lg p-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {missingContact} patients have no contact info yet
            </span>
          </div>
        )}
      </div>

      {/* Action */}
      <Button onClick={onFinish} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
        Go to Triage
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>

      <Link
        to="/import-history"
        onClick={onFinish}
        className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
      >
        <History className="h-4 w-4" />
        View import history
      </Link>
    </div>
  );
};

// ERROR STEP
const ErrorStep = ({ error, onRetry }) => {
  return (
    <div className="py-6 text-center">
      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
        <FileWarning className="h-6 w-6 text-red-600" />
      </div>
      <p className="text-sm font-medium text-slate-900 mb-2">
        {error.type === 'NO_PATIENT_IDENTIFIER' ? 'No patient names found' : 'Upload failed'}
      </p>
      <p className="text-xs text-slate-500 mb-6 max-w-xs mx-auto">
        {error.message}
      </p>
      <Button onClick={onRetry} variant="outline">
        Try Another File
      </Button>
    </div>
  );
};

export { SelectStep, PreviewStep, ImportingStep, SummaryStep };
export default UploadWizard;
